import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from "zod";
import PersonForm from "./Form";
import { useDivisionContext } from "../../../Contexts/DivisionContext";
import lawyeredLogo from "../../Assets/TeleCourt.png";
import FileUpload, { FileUploadState } from "./FileUpload";
import { LoadingDots } from "../../utilities/Utilities";
import { Spin } from 'antd';


const formSchema = z.object({
  firstName: z.string().min(1, "First Name is required"),
  lastName: z.string().min(1, "Last Name is required"),
  middleName: z.string().optional(),
  email: z.string().email("Invalid email address"),
  mobile: z.string().min(10, "Invalid mobile number"),
  address: z.string().min(1, "Address is required"),
  
});

export type FormState = z.infer<typeof formSchema>;

interface FormSectionProps {
  children: React.ReactNode;
  isVisible: boolean;
}

const FormSection: React.FC<FormSectionProps> = ({ children, isVisible }) => {
  return isVisible ? <div>{children}</div> : null;
};

const VcApplication: React.FC = () => {
  const [claimantForms, setClaimantForms] = useState<FormState[]>([
    { firstName: "", lastName: "", middleName: "", email: "", mobile: "", address: "" },
  ]);
  const [defendantForms, setDefendantForms] = useState<FormState[]>([
    { firstName: "", lastName: "", middleName: "", email: "", mobile: "", address: "" },
  ]);
  const [defendantWitnessForms, setDefendantWitnessForms] = useState<FormState[]>([
    { firstName: "", lastName: "", middleName: "", email: "", mobile: "", address: "" },
  ]);
  const [claimantWitnessForms, setClaimantWitnessForms] = useState<FormState[]>([
    { firstName: "", lastName: "", middleName: "", email: "", mobile: "", address: "" },
  ]);
  const [isLoading, setIsLoading] = useState(false)
  const [currentSection, setCurrentSection] = useState(0);
  const [applicantRole, setApplicantRole] = useState<string>("");
  const [applicantRoleName, setApplicantRoleName] = useState<string>("");
  const [processType, setProcessType] = useState<string>("");
  const [processTypeName, setProcessTypeName] = useState<string>("");
  const [caseType, setCaseType] = useState<string>("");
  const [caseTypeName, setCaseTypeName] = useState<string>("");
  const [suitNumber, setsuitNumber] = useState<string>("");
  const [judicialDivision, setJudicialDivision] = useState<string>("");
  const [judicialDivisionName, setJudicialDivisionName] = useState<string>("");
  const [courtAssigned, setCourtAssigned] = useState<string>("");
  const [courtAssignedName, setCourtAssignedName] = useState<string>("");
  const [errors, setErrors] = useState<string[]>([]);
  const [courts, setCourts] = useState<any[]>([]);
  const [caseTypeData, setCaseTypeData] = useState<any[]>(() => {
    const savedCaseTypeData = sessionStorage.getItem('caseTypeData');
    return savedCaseTypeData ? JSON.parse(savedCaseTypeData) : [];
  });
  const [fileUpload, setFileUpload] = useState<FileUploadState[]>([
    { file: null, name: '', description: '', ext: ''}
  ]);
  const [agree, setAgree] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { divisionData, fetchDivisionData, loading, currentPage } = useDivisionContext();
  const token = sessionStorage.getItem('token');
  const navigate = useNavigate();
  
  useEffect(() => {
    fetchDivisionData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCaseTypeData = async () => {
      try {
        const response = await fetch('https://backend-kyw8.onrender.com/case-types', {
          method: 'GET',
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Case type not found');
        }
        const data = await response.json();
        setCaseTypeData(data.caseTypes || []);
        sessionStorage.setItem('caseTypeData', JSON.stringify(data.caseTypes || []));
        console.log(data.caseTypes);
      } catch (error) {
        console.error('Error fetching case type', error);
      }
    };

    if (caseTypeData.length === 0) {
      fetchCaseTypeData();
    }
  }, [caseTypeData.length, token]);

  if (loading) {
    return <Spin size="large" />; 
  }

  const validateForms = (forms: FormState[]): string[] => {
    const errors: string[] = [];
    forms.forEach((form, index) => {
      const result = formSchema.safeParse(form);
      if (!result.success) {
        result.error.errors.forEach(error => {
          errors.push(`Form ${index + 1}: ${error.message}`);
        });
      }
    });
    return errors;
  };  

  const nextSection = () => {
    let formErrors: string[] = [];
    
    if (currentSection === 0) {
      // Validate all relevant forms for section 0
      formErrors = validateForms(claimantForms.concat(defendantForms));
      
      // Add validation for additional fields if needed
      if (!applicantRole) formErrors.push("Applicant Role is required");
      if (!processType) formErrors.push("Process Type is required");
        
    } else if (currentSection === 1) {
      // Validate all relevant forms for section 1
      formErrors = validateForms(claimantWitnessForms.concat(defendantWitnessForms));
      if (!caseType) formErrors.push("Case Type is required");
      if (!suitNumber) formErrors.push("Suit Number is required");
      if (!judicialDivision) formErrors.push("Judicial Division is required");
      if (!courtAssigned) formErrors.push("Court Assigned is required");
    } else if (currentSection === 2) {
      if (!agree) formErrors.push("Agree to terms and condition")
    }
  
    if (formErrors.length > 0) {
      setErrors(formErrors);
    } else {
      setErrors([]);
      setCurrentSection((prev) => prev + 1);
    }
  };
  

  const prevSection = () => {
    setCurrentSection((prev) => prev - 1);
  };

  const getUploadUrl = async (contentType: string) => {
    const response = await fetch(
      "https://backend-kyw8.onrender.com/cases/documents/upload-url",
      {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`, // Use the correct token here
          },
          body: JSON.stringify({ contentType }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error ?? "An error occurred");
    }
    console.log(data);

    return data;
  };

  const uploadToS3 = async (URL: string, file: Blob, contentType: string) => {
      const response = await fetch(URL, {
          method: "PUT",
          headers: {
              "Content-Type": contentType,
          },
          body: file,
      });

      if (!response.ok) {
          throw new Error("An error occurred while uploading the file(s)");
      }
      console.log("File uploaded successfully");
  };


  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    console.log("Claimant Forms submitted:", claimantForms);
    console.log("Defendant Forms submitted:", defendantForms);
    console.log("Claimant Witness Forms submitted:", claimantWitnessForms);
    console.log("Defendant Witness Forms submitted:", defendantWitnessForms);
    console.log("Additional Data:", { applicantRole, processType, caseType, suitNumber, judicialDivision, courtAssigned });

    const fileUploads: { file: string, name: string, description: string }[] = [];

    try {
        // Upload each file
        for (const file of fileUpload) {
            if (file.file) {
                const uploadData = await getUploadUrl(file.file.type);
                const uploadURL = uploadData?.uploadURL;
                const fileURL = uploadData?.fileURL;

                if (uploadURL && fileURL) {
                    await uploadToS3(uploadURL, file.file, file.file.type);
                    console.log("File uploaded successfully:", file.file.name);

                    // Push the object with file URL, name, and description to the uploads array
                    fileUploads.push({
                        file: fileURL,
                        name: file.name,
                        description: file.description,
                    });
                    console.log(fileUploads);
                } else {
                    throw new Error("Upload URL or file URL not provided by the server.");
                }
            }
        }

        // Prepare form data for the backend
        const formData = {
            claimantsForms: claimantForms,
            defendantForms: defendantForms,
            claimantWitnessForms: claimantWitnessForms,
            defendantWitnessForms: defendantWitnessForms,
            applicantRole,
            processType,
            caseType,
            suitNumber,
            judicialDivision,
            courtAssigned,
            fileUploads
          };

        // Send the final form data to the backend
        const response = await fetch('https://backend-kyw8.onrender.com/apply-for-virtual-hearing', {
            method: 'POST',
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
        });

        if (!response.ok) {
            const error = await response.json();
            console.log(error);
            throw new Error(error.message);
        }

        const data = await response.json();
        toast.success('Form submitted successfully!', {
            position: 'top-center',
        });
        console.log('Form submitted successfully:', data);
        navigate('/');
    } catch (error) {
        console.error('Error submitting form:', error);
        toast.error('An unexpected error occurred. Please try again later.', {
            position: 'bottom-right',
        });
    } finally {
        setIsLoading(false);
    }
  };



  const handleDivisionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const selectedDivisionId = event.target.value;
    setJudicialDivision(selectedDivisionId);

    // Find the selected division data
    const selectedDivisionData = divisionData.find((division: any) => division.basicData.id === selectedDivisionId);

    if (selectedDivisionData) {
        setJudicialDivisionName(selectedDivisionData.basicData.name || ''); // Access the name from the found division
        setCourts(selectedDivisionData.courts);
    } else {
        setJudicialDivisionName(''); // Reset name if no division found
        setCourts([]);
    }
  };

  const handleAgreeChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAgree(event.target.checked);
  };

  return (
    <div className='bg-white w-full p-10 flex flex-col' 
      style={{ backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)), url(${lawyeredLogo})`,
      backgroundSize: '40%',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat' }}>
      <form onSubmit={handleSubmit}>
        <FormSection isVisible={currentSection === 0}>
          <h2 className="form-header">Applicant Information</h2>
          <div className="flex flex-col lg:flex-row gap-5 lg:gap-10 mt-10 lg:w-full w-full">
            <div className="flex gap-2 w-[50%]">
              <label htmlFor="applicant-role" className="w-[50%]">Applicant Role</label>
              <select 
                name="Applicant Role"
                id="applicant-role"
                value={applicantRole}
                className="vc-reg-input"
                onChange={(e) => {
                  setApplicantRole(e.target.value);
                  setApplicantRoleName(e.target.options[e.target.selectedIndex].text);}}
                >
                  <option value="">Select an option</option>
                  <option value="claimantCounsel">Claimant Counsel</option>
                  <option value="defendantCounsel">Defendant Counsel</option>
              </select>
            </div>
            <div className="flex gap-2 w-[50%]">
              <label htmlFor="processType" className="w-[80%]">Mode of Process</label>
                <select 
                    name="Process Type" 
                    id="processType" 
                    value={processType}
                    className="vc-reg-input"
                    onChange={(e) => {
                      setProcessType(e.target.value)
                      setProcessTypeName(e.target.options[e.target.selectedIndex].text);
                    }}>
                    <option value="">Select an option</option>
                    <option value="fastTrack">Fast Track</option>
                    <option value="normal">Normal</option>
                </select>
            </div>
          </div>
          
          <PersonForm forms={claimantForms} setForms={setClaimantForms} title="Claimant" />
          <PersonForm forms={defendantForms} setForms={setDefendantForms} title="Defendant" />
          {errors.length > 0 && (
              <div>
                {errors.map((error, index) => (
                  <div key={index} className="text-red-500">{error}</div>
                  ))}
              </div>
            )}
        </FormSection>
        <FormSection isVisible={currentSection === 1}>
          <h2 className="form-subheader">Case Details</h2>
          <div className="flex flex-col md:flex-row gap-5">
            <div className="flex flex-col">
              <label htmlFor="caseType">Case Type</label>
              <select 
                  name="Case Type" 
                  id="caseType" 
                  value={caseType}
                  className="vc-reg-input"
                  onChange={(e) => {
                    setCaseType(e.target.value);
                    setCaseTypeName(e.target.options[e.target.selectedIndex].text);
                  }}>
                  <option value="" disabled>Select an option</option>
                  {caseTypeData.map((type: any) => (
                  <option key={type.id} value={type.id}>{type.title}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label htmlFor="suitNumber">Suit No:</label>
              <input 
                  type="text"
                  id="suitNumber"
                  placeholder="XX/YYY/XX/YYYY"
                  value={suitNumber}
                  className="vc-reg-input"
                  onChange={(e) => setsuitNumber(e.target.value)}
              />
            </div>
            <div className="flex flex-col">
                <label htmlFor="judicialDivision">Judicial Division</label>
                <select
                  name="judicialDivision"
                  id="judicialDivision"
                  value={judicialDivision}
                  className="vc-reg-input"
                  onChange={handleDivisionChange}>
                    <option value="" disabled>Select an option</option>
                    {divisionData && divisionData.map((division: any) => (
                      <option key={division.basicData.id} value={division.basicData.id}>
                        {division.basicData.name}
                      </option>
                    ))}
              </select>
            </div>
            <div className="flex flex-col">
              <label htmlFor="courtAssigned">Judge Assigned</label>
              <select 
                  name="courtAssigned" 
                  id="courtAssigned" 
                  value={courtAssigned}
                  className="vc-reg-input"
                  onChange={(e) => {
                    setCourtAssigned(e.target.value); 
                    setCourtAssignedName(e.target.options[e.target.selectedIndex].text);
                  }}>
                      <option value="" disabled>Select an option</option>
                      {courts.map((court) => (
                        <option key={court.id} value={court.id}> Hon. Justice {''}
                          {court.judge?.firstName} {court.judge?.lastName}
                        </option>
                      ))}
              </select>
            </div>
          </div>
          <PersonForm forms={claimantWitnessForms} setForms={setClaimantWitnessForms} title="Claimant Witness" />
          <PersonForm forms={defendantWitnessForms} setForms={setDefendantWitnessForms} title="Defendant Witness" />
          {errors.length > 0 && (
              <div>
                {errors.map((error, index) => (
                  <div key={index} className="text-red-500">{error}</div>
                  ))}
              </div>
            )}
        </FormSection>

        <FormSection isVisible={currentSection === 2}>
          <div>
            <label htmlFor="" className="form-subheader">Upload Case Documents</label>
            <FileUpload fileUploads={fileUpload} setFileUploads={setFileUpload} />
          </div>
          <p className="mt-10 font-bold">I have read and consented to the provisions of the rules for virtual hearing for 
            <Link to={''}><span className="text-red-800"> Courts</span><br/></Link>
            I undertake to remain bound by the same to the extent applicable to me. I agree to pay all 
            the charges directed by the court and use this platform
          </p>
          <div className="flex gap-1">
          <input
              type="checkbox"
              checked={agree}
              onChange={handleAgreeChange}
            />
            <p>I agree</p>
          </div>
          {errors.length > 0 && (
              <div>
                {errors.map((error, index) => (
                  <div key={index} className="text-red-500">{error}</div>
                  ))}
              </div>
            )}

        </FormSection>

        <FormSection isVisible={currentSection === 3}>
          <h2 className="form-header text-center mb-10 text-2xl font-bold">Review Your Application</h2>
          <div className="flex flex-col mx-20 space-y-8">
            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">Applicant Information</h3>
              <div className="flex gap-10">
                <p className="flex gap-2"><strong>Applicant Role:</strong> <span>{applicantRoleName}</span></p>
                <p className="flex gap-2"><strong>Process Type:</strong> <span>{processTypeName}</span></p>
              </div>
            </div>

            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">Case Details</h3>
              <div className="flex flex-col space-y-2">
                <p className="flex gap-2"><strong>Case Type:</strong> <span>{caseTypeName}</span></p>
                <p className="flex gap-2"><strong>SUIT NUMBER:</strong> <span>{suitNumber}</span></p>
                <p className="flex gap-2"><strong>Judicial Division:</strong> <span>{judicialDivisionName}</span></p>
                <p className="flex gap-2"><strong>Court Assigned:</strong> <span>{courtAssignedName}</span></p>
              </div>
            </div>

            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">Claimant Information</h3>
              {claimantForms.map((form, index) => (
                <div key={index} className="border-b border-gray-200 pb-2 mb-2 last:mb-0">
                  <p className="flex gap-2"><strong>First Name:</strong> <span>{form.firstName}</span></p>
                  <p className="flex gap-2"><strong>Last Name:</strong> <span>{form.lastName}</span></p>
                  <p className="flex gap-2"><strong>Middle Name:</strong> <span>{form.middleName}</span></p>
                  <p className="flex gap-2"><strong>Email:</strong> <span>{form.email}</span></p>
                  <p className="flex gap-2"><strong>Mobile:</strong> <span>{form.mobile}</span></p>
                  <p className="flex gap-2"><strong>Address:</strong> <span>{form.address}</span></p>
                </div>
              ))}
            </div>

            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">Defendant Information</h3>
              {defendantForms.map((form, index) => (
                <div key={index} className="border-b border-gray-200 pb-2 mb-2 last:mb-0">
                  <p className="flex gap-2"><strong>First Name:</strong> <span>{form.firstName}</span></p>
                  <p className="flex gap-2"><strong>Last Name:</strong> <span>{form.lastName}</span></p>
                  <p className="flex gap-2"><strong>Middle Name:</strong> <span>{form.middleName}</span></p>
                  <p className="flex gap-2"><strong>Email:</strong> <span>{form.email}</span></p>
                  <p className="flex gap-2"><strong>Mobile:</strong> <span>{form.mobile}</span></p>
                  <p className="flex gap-2"><strong>Address:</strong> <span>{form.address}</span></p>
                </div>
              ))}
            </div>

            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">Claimant Witness Information</h3>
              {claimantWitnessForms.map((form, index) => (
                <div key={index} className="border-b border-gray-200 pb-2 mb-2 last:mb-0">
                  <p className="flex gap-2"><strong>First Name:</strong> <span>{form.firstName}</span></p>
                  <p className="flex gap-2"><strong>Last Name:</strong> <span>{form.lastName}</span></p>
                  <p className="flex gap-2"><strong>Middle Name:</strong> <span>{form.middleName}</span></p>
                  <p className="flex gap-2"><strong>Email:</strong> <span>{form.email}</span></p>
                  <p className="flex gap-2"><strong>Mobile:</strong> <span>{form.mobile}</span></p>
                  <p className="flex gap-2"><strong>Address:</strong> <span>{form.address}</span></p>
                </div>
              ))}
            </div>

            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">Defendant Witness Information</h3>
              {defendantWitnessForms.map((form, index) => (
                <div key={index} className="border-b border-gray-200 pb-2 mb-2 last:mb-0">
                  <p className="flex gap-2"><strong>First Name:</strong> <span>{form.firstName}</span></p>
                  <p className="flex gap-2"><strong>Last Name:</strong> <span>{form.lastName}</span></p>
                  <p className="flex gap-2"><strong>Middle Name:</strong> <span>{form.middleName}</span></p>
                  <p className="flex gap-2"><strong>Email:</strong> <span>{form.email}</span></p>
                  <p className="flex gap-2"><strong>Mobile:</strong> <span>{form.mobile}</span></p>
                  <p className="flex gap-2"><strong>Address:</strong> <span>{form.address}</span></p>
                </div>
              ))}
            </div>

            <div className="border rounded-lg p-5 shadow-md">
              <h3 className="form-subheader text-lg font-semibold mb-2">File Uploads</h3>
              {fileUpload.map((upload, index) => (
                <div key={index} className="border-b border-gray-200 pb-2 mb-2 last:mb-0">
                  <p className="flex gap-2"><strong>File Name:</strong> <span>{upload.name}</span></p>
                  <p className="flex gap-2"><strong>Description:</strong> <span>{upload.description}</span></p>
                  <p className="flex gap-2"><strong>File:</strong> <span>{upload.file?.name}</span></p>
                </div>
              ))}
            </div>
          </div>
        </FormSection>

        <div className="mt-20 flex justify-between">
          {currentSection > 0 && (
            <button type="button" onClick={prevSection}>
              {'<< Previous'}
            </button>
          )}
          {currentSection <= 2 ? (
            <button type='button' onClick={(event) => {
              event.preventDefault();
              nextSection();
            }}>
              {'Next >>'}
            </button>
          ) : (
            <button
              className="button-primary !w-fit"
              type="submit">{isLoading ? <LoadingDots /> : 'Submit'}</button>
          )}
        </div>
      </form>
    </div>
  );
};

export default VcApplication;